import React from 'react';
import type { ReactNode } from 'react';

import type { FaqEntry } from '@/data/shared/faq';

import Area from '@/components/apps/layouts/shared/Area';
import SectionTitle from '@/components/apps/ui/title/SectionTitle';

import { SolutionFaqItem } from './SolutionFaqItem';
import {
  SECTION_TITLE_COLOR_BY_TONE,
  SURFACE_BACKGROUND_CLASS_BY_TONE,
  type SolutionSurfaceTone,
} from './solutionSurfaceTone';

/**
 * ### SolutionFaqSectionProps
 *
 * Copy, entries, analytics identity, and surface tone of a FAQ section. The
 * section renders exactly what it is given; localisation and data selection
 * belong to the page that places it.
 */
export type SolutionFaqSectionProps = {
  /**
   * ### title
   *
   * Section heading, already translated.
   */
  title: ReactNode;

  /**
   * ### description
   *
   * One-line lead under the heading. Omitting it removes the lead element
   * entirely rather than rendering an empty one.
   */
  description?: ReactNode;

  /**
   * ### items
   *
   * Entries rendered in order. Pass the same list the page emits as FAQPage
   * structured data so the rendered copy and the schema stay identical.
   */
  items: ReadonlyArray<FaqEntry>;

  /**
   * ### analyticsSectionId
   *
   * Value of `data-analytics-section`, the `section_id` reported when the
   * section scrolls into view. Unique within the page.
   */
  analyticsSectionId: string;

  /**
   * ### tone
   *
   * Surface tone that decides background, text, divider, and icon colors.
   *
   * @defaultValue `'dark'` — the solution-page surface
   */
  tone?: SolutionSurfaceTone;
};

/**
 * ### SolutionFaqSection
 *
 * Renders a FAQ list as native details/summary disclosures under a section
 * title, so every answer is present in the server-rendered HTML whether or not
 * it is expanded. Shared by the solution and home pages; each page supplies
 * its own copy and picks the surface tone it sits on.
 *
 * @param props Copy, entries, analytics identity, and surface tone
 * @returns The rendered FAQ section element
 * @see {@link SolutionFaqSectionProps} Defaults applied to omitted props
 */
export function SolutionFaqSection({
  title,
  description,
  items,
  analyticsSectionId,
  tone = 'dark',
}: SolutionFaqSectionProps) {
  return (
    <section
      className={`${SURFACE_BACKGROUND_CLASS_BY_TONE[tone]} py-[100px] lg:py-[clamp(100px,14.286vw,200px)]`}
      data-analytics-section={analyticsSectionId}
    >
      <Area>
        <SectionTitle
          color={SECTION_TITLE_COLOR_BY_TONE[tone]}
          title={title}
          description={description}
        />

        <div className="mt-10 lg:mt-[clamp(40px,5vw,60px)]">
          {items.map((entry) => (
            <SolutionFaqItem key={entry.question} entry={entry} tone={tone} />
          ))}
        </div>
      </Area>
    </section>
  );
}
